import { isProductionTargetBranch, type MergeBranchMutationInput, mergeBranchMutation } from 'app/branches/api';
import { createRoutine } from 'app/common/utils/new-routines';
import { getRoutineSaga } from 'app/common/utils/new-sagas';
import type { SagaIterator } from 'redux-saga';
import { all, call, put, takeLatest } from 'redux-saga/effects';
import type { ActionType } from 'typesafe-actions';
import { showMessage } from '../../messages/actions.js';
import {
  closeBranchMerge,
  setBranchMergeInProgress,
  setCurrentBranch,
  setCurrentBranchCanPromote,
  setCurrentBranchNeedsUpdate
} from '../actions';
import { DEFAULT_BRANCH } from '../constants.js';
import { useWorkingCopyStore } from '../store/working-copy-store.js';
import { fetchSchemaRoutine } from './fetch-schema.js';

export const mergeBranchRoutine = createRoutine('branches/MERGE_BRANCH')<
  MergeBranchMutationInput,
  undefined,
  undefined,
  undefined,
  string,
  undefined
>();

export function* mergeBranchSaga(_payload: undefined, input: MergeBranchMutationInput): SagaIterator {
  if (!input.dryRun) {
    yield put(setBranchMergeInProgress(true));
  }

  return yield call(mergeBranchMutation, input);
}

export function* mergeBranchFulfilledSaga({
  meta: { target, dryRun }
}: ActionType<typeof mergeBranchRoutine.fulfilled>): SagaIterator {
  if (!dryRun) {
    const isProductionTarget = isProductionTargetBranch(target);
    if (isProductionTarget) {
      yield put(setCurrentBranch({ branch: DEFAULT_BRANCH, snackbarMessage: false }));
    }

    yield put(
      fetchSchemaRoutine.requested(undefined, {
        branch: isProductionTarget ? DEFAULT_BRANCH : target.branchName,
        runtime: false
      })
    );
    yield put(
      fetchSchemaRoutine.requested(undefined, {
        branch: isProductionTarget ? DEFAULT_BRANCH : target.branchName,
        runtime: true
      })
    );

    yield put(closeBranchMerge());
    yield put(setBranchMergeInProgress(false));
    yield put(showMessage('info', `${isProductionTarget ? 'Promotion' : 'Update'} successful`, 3000));
    if (isProductionTarget) {
      yield put(setCurrentBranchCanPromote(false));
    } else {
      yield put(setCurrentBranchNeedsUpdate(false));
    }

    // In case they had working copy changes and accepted that they would lose them
    yield call(useWorkingCopyStore.getState().reset);
  }
}

export function* mergeBranchRejectedSaga({
  payload,
  meta: { head, dryRun }
}: ActionType<typeof mergeBranchRoutine.rejected>): SagaIterator {
  if (!dryRun) {
    console.error(`Failed to merge branch "${head.branchName}":\n\n`, payload);
    yield put(setBranchMergeInProgress(false));
    yield put(showMessage('error', 'Failed to merge branch. Please see dev console for more details.'));
  }
}

export default function* root(): SagaIterator {
  yield all([
    takeLatest(mergeBranchRoutine.requested, getRoutineSaga(mergeBranchRoutine, mergeBranchSaga)),
    takeLatest(mergeBranchRoutine.fulfilled, mergeBranchFulfilledSaga),
    takeLatest(mergeBranchRoutine.rejected, mergeBranchRejectedSaga)
  ]);
}
